import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  createPaymentType,
  updatePaymentType,
} from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import { PaymentType } from "../../features/payment-types/models/PaymentType";
import {
  minorUnitsToInputString,
  parseMoneyToMinorUnits,
  SUPPORTED_CURRENCIES,
  currencySymbol,
} from "../../features/billing/utils/money";
import {
  selectInstructorPreferences,
  selectPreferencesInitializationStatus,
} from "../../features/instructor-preferences/instructor-preferences-slice";
import { fetchSchedulePreferences } from "../../features/instructor-preferences/slice-extensions/instructor-preferences-slice-extension";

interface PaymentTypeFormProps {
  /** When set, the form edits this payment type (currency is fixed). */
  paymentType?: PaymentType;
  /** Receives the saved payment type, so callers can e.g. preselect it. */
  onSaved: (saved: PaymentType) => void;
  onClose: () => void;
}

/** Create or edit a payment type: name + price, plus currency on create. */
const PaymentTypeForm: React.FC<PaymentTypeFormProps> = ({ paymentType, onSaved, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const preferences = useAppSelector(selectInstructorPreferences);
  const preferencesInit = useAppSelector(selectPreferencesInitializationStatus);

  const isEdit = Boolean(paymentType);
  const [name, setName] = useState(paymentType?.name ?? "");
  const [price, setPrice] = useState(
    paymentType ? minorUnitsToInputString(paymentType.priceAmountMinorUnits, paymentType.currency) : ""
  );
  const [currency, setCurrency] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (preferencesInit === "not-initialized") {
      dispatch(fetchSchedulePreferences());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Preselect the instructor's preferred currency once preferences arrive,
  // without clobbering an explicit pick.
  useEffect(() => {
    if (!currency && preferences?.currency) {
      setCurrency(preferences.currency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences?.currency]);

  const currencyOptions = SUPPORTED_CURRENCIES.includes(currency) || !currency
    ? SUPPORTED_CURRENCIES
    : [currency, ...SUPPORTED_CURRENCIES];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const minorUnits = parseMoneyToMinorUnits(price, paymentType?.currency ?? currency);
    if (!name.trim() || Number.isNaN(minorUnits)) {
      setError(t("billing.paymentTypeForm.errorRequired"));
      return;
    }
    try {
      const saved = isEdit && paymentType
        ? await dispatch(updatePaymentType({
            id: paymentType.id,
            request: { name: name.trim(), priceAmountMinorUnits: minorUnits },
          })).unwrap()
        : await dispatch(createPaymentType({
            name: name.trim(),
            priceAmountMinorUnits: minorUnits,
            currency: currency || undefined,
          })).unwrap();
      onSaved(saved);
    } catch (err) {
      setError((err as { message?: string })?.message ?? t("billing.paymentTypeForm.errorSave"));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 lg:p-6">
      <h3>
        {isEdit
          ? t("billing.paymentTypeForm.editTitle", { name: paymentType!.name })
          : t("billing.actions.newPaymentType")}
      </h3>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          className="input"
          placeholder={t("billing.paymentTypeForm.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="input sm:max-w-[160px]"
          placeholder={t("billing.paymentTypeForm.pricePlaceholder")}
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        {!isEdit && (
          <select
            className="input sm:max-w-[150px]"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            aria-label={t("billing.paymentTypeForm.currency")}
          >
            {currencyOptions.map((code) => (
              <option key={code} value={code}>
                {currencySymbol(code)} {code}
              </option>
            ))}
          </select>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
      <div className="mt-4 flex justify-end gap-3">
        <button type="button" className="btn btn-secondary" onClick={onClose}>{t("billing.common.cancel")}</button>
        <button type="submit" className="btn btn-primary">
          {isEdit ? t("billing.common.saveChanges") : t("billing.paymentTypeForm.add")}
        </button>
      </div>
    </form>
  );
};

export default PaymentTypeForm;
